import React, { useState, useEffect } from 'react';
import { Button, Modal, Space, Tag, Typography, Spin, Divider, Alert } from 'antd';
import { DeleteOutlined, PlusOutlined, ExperimentOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { deleteTest, fetchTests } from '../../../../../../api/healthcare/Testing/TestApi';
import CreateTestModal from '../../../../results/CreateTestOrderModal';
import { Test } from '../../../../../../types/healthcare/TestTypes';
import dayjs from 'dayjs';
import type { UserUuid } from '../../../../../../types/identity';

const { Text } = Typography;

interface Props {
  taskConfig: Record<string, any>;
  patientId: UserUuid;
  onChange: (payload: Record<string, any>, isValid: boolean) => void;
}

const PRIORITY_COLOR: Record<string, string> = {
  stat: 'red',
  urgent: 'orange',
  routine: 'blue',
};

const STATUS_COLOR: Record<string, string> = {
  pending: 'orange',
  ordered: 'orange',
  in_progress: 'blue',
  completed: 'green',
  reviewed: 'green',
  results_available: 'green',
  results_reviewed: 'green',
  cancelled: 'default',
};

// Statuses that indicate results have been attached to the order
const HAS_RESULTS_STATUSES = new Set(['completed', 'reviewed', 'results_reviewed', 'results_available']);

const hasResults = (t: Test) => HAS_RESULTS_STATUSES.has((t.status ?? '').toLowerCase());

const CreateTestOrderTaskForm: React.FC<Props> = ({ taskConfig, patientId, onChange }) => {
  const [orders, setOrders]               = useState<Test[]>([]);
  const [drawerOpen, setDrawerOpen]       = useState(false);
  const [currentOrder, setCurrentOrder]   = useState<Partial<Test>>({});
  const [existingTests, setExistingTests] = useState<Test[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const suggestedTests: string[]     = taskConfig.suggested_test_names ?? [];
  const allowedPackageIds: number[]  = taskConfig.allowed_test_package_ids ?? [];
  const requiredTests: string[]      = taskConfig.required_blood_tests ?? [];

  const isAllowedOrder = (t: Test) =>
    allowedPackageIds.length === 0 ||
    (t.custom_test_packages ?? []).some((pkg) => allowedPackageIds.includes(pkg.id));

  const hasRequiredPackageOrder = (currentExistingTests: Test[], currentNewOrders: Test[]) => {
    const allOrders = [...currentExistingTests, ...currentNewOrders];
    if (allowedPackageIds.length === 0) {
      return allOrders.some(hasResults);
    }
    return allOrders.filter(isAllowedOrder).some(hasResults);
  };

  useEffect(() => {
    fetchTests(patientId)
      .then((tests) => {
        setExistingTests(tests);
        onChange({ test_ids: [] }, hasRequiredPackageOrder(tests, []));
      })
      .catch(() => {})
      .finally(() => setLoadingExisting(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const openAddDrawer = () => {
    setCurrentOrder({ patient_id: patientId, priority: 'routine' });
    setDrawerOpen(true);
  };

  const openEditDrawer = (test: Test) => {
    setCurrentOrder(test);
    setDrawerOpen(true);
  };

  const handleSave = (saved: Test) => {
    if (currentOrder.id && currentOrder.id > 0) {
      // Editing an existing order — update existingTests and recheck validity
      const updated = existingTests.map((t) => (t.id === saved.id ? saved : t));
      setExistingTests(updated);
      setDrawerOpen(false);
      onChange({ test_ids: orders.map((o) => o.id) }, hasRequiredPackageOrder(updated, orders));
    } else {
      // New order added this session
      const newOrders = [...orders, saved];
      setOrders(newOrders);
      setDrawerOpen(false);
      onChange({ test_ids: newOrders.map((o) => o.id) }, hasRequiredPackageOrder(existingTests, newOrders));
    }
  };

  const handleRemove = (order: Test) => {
    Modal.confirm({
      title: 'Remove Test Order',
      content: 'This will delete the test order. Are you sure?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try { await deleteTest(order.id); } catch {}
        const newOrders = orders.filter((o) => o.id !== order.id);
        setOrders(newOrders);
        onChange({ test_ids: newOrders.map((o) => o.id) }, hasRequiredPackageOrder(existingTests, newOrders));
      },
    });
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={6}>

      {requiredTests.length > 0 && (
        <div style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--mg-warning-border, #faad14)', background: 'var(--mg-warning-bg, #fffbe6)' }}>
          <Text style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4, color: 'var(--mg-warning-text, #ad6800)' }}>Required Blood Tests</Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {requiredTests.map((t) => (
              <Tag key={t} color="gold" style={{ margin: 0, fontSize: 10, padding: '0 5px' }}>{t.replace(/_/g, ' ')}</Tag>
            ))}
          </div>
        </div>
      )}

      {/* Existing test orders */}
      <div style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--mg-border)', background: 'var(--mg-bg-subtle)' }}>
        {(() => {
          const filteredExisting = existingTests.filter(isAllowedOrder);
          const awaitingResults  = filteredExisting.length > 0 && !filteredExisting.some(hasResults);

          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <ExperimentOutlined style={{ color: 'var(--mg-text-secondary)', fontSize: 11 }} />
                <Text style={{ fontSize: 11, fontWeight: 600, color: 'var(--mg-text)' }}>Existing Test Orders</Text>
                {!loadingExisting && (
                  <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>({filteredExisting.length})</Text>
                )}
              </div>

              {loadingExisting ? (
                <Spin size="small" />
              ) : filteredExisting.length === 0 ? (
                <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>No existing test orders on file.</Text>
              ) : (
                <>
                  {awaitingResults && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 8, fontSize: 11, padding: '4px 8px' }}
                      message="Test ordered — awaiting results. Open an order below to attach results when they arrive."
                    />
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {filteredExisting.map((t) => (
                      <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--mg-border)', background: 'var(--mg-bg)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <Text style={{ fontSize: 12, fontWeight: 600 }}>{t.custom_test_packages?.map((p) => p.name).join(', ') || t.test_name || 'Test Order'}</Text>
                            <Tag color={PRIORITY_COLOR[t.priority] ?? 'blue'} style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{t.priority ?? 'routine'}</Tag>
                            <Tag color={STATUS_COLOR[(t.status ?? '').toLowerCase()] ?? 'default'} style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{t.status}</Tag>
                            {t.test_category && <Tag style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{t.test_category}</Tag>}
                          </div>
                          {t.created_at && (
                            <Text style={{ fontSize: 10, color: 'var(--mg-text-secondary)', display: 'block' }}>
                              {dayjs(t.created_at).format('DD MMM YYYY')}
                            </Text>
                          )}
                        </div>
                        <Button
                          type="link"
                          size="small"
                          icon={<FolderOpenOutlined />}
                          onClick={() => openEditDrawer(t)}
                          style={{ padding: '0 4px', fontSize: 11 }}
                        >
                          Open
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          );
        })()}
      </div>

      <Divider style={{ margin: '2px 0' }} />

      {/* New test orders */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 11, fontWeight: 600, color: 'var(--mg-text)' }}>New Test Orders</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddDrawer}>
          Add
        </Button>
      </div>

      {suggestedTests.length > 0 && (
        <Space wrap size={4}>
          <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>Suggested:</Text>
          {suggestedTests.map((t) => <Tag key={t} style={{ fontSize: 11, margin: 0 }}>{t}</Tag>)}
        </Space>
      )}

      {orders.length === 0 ? (
        <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)', display: 'block', padding: '4px 0' }}>
          No new orders — existing orders will be submitted.
        </Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {orders.map((order) => (
            <div key={order.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--mg-border)', background: 'var(--mg-bg)' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <Text style={{ fontSize: 12, fontWeight: 600 }}>{order.custom_test_packages?.map((p) => p.name).join(', ') || order.test_name || 'Test Order'}</Text>
                <Tag color={PRIORITY_COLOR[order.priority] ?? 'blue'} style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{order.priority ?? 'routine'}</Tag>
                {order.test_category && <Tag style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{order.test_category}</Tag>}
              </div>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemove(order)} />
            </div>
          ))}
        </div>
      )}

      <CreateTestModal
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        currentOrder={currentOrder}
        setCurrentOrder={setCurrentOrder as React.Dispatch<React.SetStateAction<Partial<Test | null>>>}
        allowedPackageIds={allowedPackageIds.length > 0 ? allowedPackageIds : undefined}
      />
    </Space>
  );
};

export default CreateTestOrderTaskForm;
